import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hh-cream flex items-center justify-center px-6 py-12" data-testid="forgot-page">
      <div className="hh-card w-full max-w-md p-10">
        <Link to="/login" className="text-xs text-hh-ink2 hover:text-hh-ink inline-flex items-center gap-1 mb-8" data-testid="back-to-login">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <span className="inline-grid place-items-center h-14 w-14 rounded-full bg-hh-sage/15 text-hh-sage mb-5">
              <CheckCircle2 size={22} />
            </span>
            <h1 className="font-serif text-2xl text-hh-ink mb-2">Check your inbox</h1>
            <p className="text-sm text-hh-ink2 leading-relaxed">
              If <strong className="text-hh-ink">{email}</strong> matches an account, we&rsquo;ve sent you a link to reset your password.
              It expires in 60 minutes.
            </p>
            <p className="mt-6 text-xs text-hh-ink2">
              Didn&rsquo;t get it? Check your spam folder, or{" "}
              <button onClick={() => setSent(false)} className="text-hh-primary hover:underline" data-testid="forgot-try-again">
                try a different email
              </button>.
            </p>
          </div>
        ) : (
          <>
            <p className="hh-overline mb-3">Author portal</p>
            <h1 className="font-serif text-3xl text-hh-ink mb-3">Reset your password</h1>
            <p className="text-sm text-hh-ink2 mb-8 leading-relaxed">
              Pop your email below and we&rsquo;ll send you a link to set a new password.
            </p>
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="block text-sm text-hh-ink2 mb-2" htmlFor="email">Email</label>
                <input
                  id="email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email"
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
                />
              </div>
              {err && (
                <div data-testid="forgot-error" className="text-sm text-hh-danger bg-[#FBEAE6] border border-[#F1CEC5] rounded-lg px-4 py-3">{err}</div>
              )}
              <button
                type="submit" disabled={loading} data-testid="forgot-submit"
                className="hh-btn-primary w-full disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Send me a reset link
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
